import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { CameraOff, Camera } from "lucide-react";
import { toast } from "sonner";

async function safeStopAndClear(inst) {
  if (!inst) return;
  try {
    const state = inst.getState?.();
    // Only attempt stop if scanning/paused
    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
      await inst.stop();
    }
  } catch {
    // ignore
  }
  try {
    await inst.clear();
  } catch {
    // ignore
  }
}

export function QrScannerSheet({ open, onOpenChange, onDetected, title = "Ler QR Code" }) {
  const containerId = "qr-scanner-container";
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);
  const [permissionError, setPermissionError] = useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let active = true;
    stoppedRef.current = false;

    const start = async () => {
      try {
        const html5 = new Html5Qrcode(containerId, /* verbose */ false);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decodedText) => {
            if (stoppedRef.current) return;
            stoppedRef.current = true;
            await safeStopAndClear(html5);
            if (!active) return;
            try { window.navigator?.vibrate?.(120); } catch {}
            try { onDetected?.(decodedText); } catch {}
            try { onOpenChange?.(false); } catch {}
          },
          () => {} // ignore individual scan errors
        );
        if (active) setRunning(true);
      } catch (e) {
        if (active) {
          setPermissionError(e?.message || "Erro ao acessar câmera");
          toast.error("Não foi possível acessar a câmera. Verifique permissões.");
        }
      }
    };

    if (open) {
      setPermissionError("");
      setRunning(false);
      const t = setTimeout(start, 280);
      return () => {
        clearTimeout(t);
        active = false;
        stoppedRef.current = true;
        const inst = scannerRef.current;
        if (inst) {
          safeStopAndClear(inst).finally(() => {
            scannerRef.current = null;
          });
        }
        setRunning(false);
      };
    }

    return () => {
      active = false;
      stoppedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] rounded-t-2xl">
        <SheetHeader>
          <SheetTitle className="font-display">{title}</SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          <div className="relative mx-auto w-full max-w-[420px] aspect-square rounded-2xl overflow-hidden bg-black ring-1 ring-[hsl(var(--gold)/0.4)]">
            <div id={containerId} className="w-full h-full" />
            {running && (
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-6 right-6 top-6 bottom-6 rounded-xl border-2 border-[hsl(var(--gold)/0.7)]" />
                <div className="absolute left-6 right-6 top-6 h-[2px] bg-[hsl(var(--gold))] scan-line" />
              </div>
            )}
            {!running && (
              <div className="absolute inset-0 flex items-center justify-center text-white/80 flex-col gap-2">
                {permissionError ? (
                  <>
                    <CameraOff className="w-10 h-10" />
                    <p className="text-xs text-center px-6">{permissionError}</p>
                  </>
                ) : (
                  <>
                    <Camera className="w-10 h-10 animate-pulse" />
                    <p className="text-xs">Iniciando câmera…</p>
                  </>
                )}
              </div>
            )}
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Posicione o QR Code do aluno dentro da moldura.
          </p>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="qr-cancel">
              Cancelar
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
